import React, { useState } from 'react';
import { CheckCircle, XCircle, AlertCircle, RefreshCw, Activity, Clock } from 'lucide-react';

const ServiceStatusDashboard = ({ services, onRefresh }) => {
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    await onRefresh?.();
    setTimeout(() => setRefreshing(false), 1200);
  };

  const getStatusIcon = (status) => {
    if (status === 'connected') return <CheckCircle className="w-4 h-4 text-green-500" />;
    if (status === 'error') return <XCircle className="w-4 h-4 text-red-500" />;
    return <AlertCircle className="w-4 h-4 text-yellow-500" />;
  };

  const getStatusBadge = (status) => {
    const base = 'px-2 py-0.5 rounded-full text-xs font-medium';
    if (status === 'connected') return `${base} bg-green-100 text-green-700`;
    if (status === 'error') return `${base} bg-red-100 text-red-700`;
    return `${base} bg-yellow-100 text-yellow-700`;
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-blue-600" />
          <h2 className="text-base font-semibold text-gray-900">Service Connection Status</h2>
        </div>
        <button
          onClick={handleRefresh}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-blue-600 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>
      <div className="divide-y divide-gray-50">
        {services?.map((svc) => (
          <div key={svc?.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${svc?.bgColor}`}>
                  <svc.icon className={`w-5 h-5 ${svc?.iconColor}`} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">{svc?.name}</span>
                    {getStatusIcon(svc?.status)}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{svc?.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-6 text-right">
                <div className="hidden md:block">
                  <p className="text-xs text-gray-400">API Rate</p>
                  <p className="text-sm font-medium text-gray-700">{svc?.apiRate}</p>
                </div>
                <div className="hidden md:block">
                  <p className="text-xs text-gray-400">Sync Perf</p>
                  <p className="text-sm font-medium text-gray-700">{svc?.syncPerf}</p>
                </div>
                <div className="hidden lg:block">
                  <p className="text-xs text-gray-400">Last Sync</p>
                  <div className="flex items-center gap-1">
                    <Clock className="w-3 h-3 text-gray-400" />
                    <p className="text-sm text-gray-600">{svc?.lastSync}</p>
                  </div>
                </div>
                <span className={getStatusBadge(svc?.status)}>
                  {svc?.status === 'connected' ? 'Connected' : svc?.status === 'error' ? 'Error' : 'Warning'}
                </span>
              </div>
            </div>
            {svc?.status !== 'connected' && (
              <div className="mt-2 ml-13 pl-13">
                <p className="text-xs text-red-500 ml-13">{svc?.errorMessage}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ServiceStatusDashboard;
